<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
        </div>
        <NevsCard class="form help-emails">
            <h2>{{ $LANG.Get('labels.sendingSettings') }}</h2>
            <NevsTextField v-model="client.email_copy" :label="$LANG.Get('fields.emailCopy') + ':'"></NevsTextField>
        </NevsCard>
        <NevsCard class="form">
            <h2>{{ $LANG.Get('labels.smtpSettings') }}</h2>
            <NevsCheckbox v-model="client.use_smtp" :label="$LANG.Get('fields.useSmtp')"></NevsCheckbox>
            <p class="disclaimer">{{ $LANG.Get('labels.smtpCustomMessageNote') }}</p>
            <template v-if="client.use_smtp">
                <NevsTextField v-model="client.smtp_host" :label="$LANG.Get('fields.smtpHost') + ':'"></NevsTextField>
                <NevsTextField v-model="client.smtp_port" :label="$LANG.Get('fields.smtpPort') + ':'"></NevsTextField>
                <NevsSelect v-model="client.smtp_encryption" :options="encryptionOptions" :label="$LANG.Get('fields.smtpEncryption') + ':'"></NevsSelect>
                <NevsTextField v-model="client.smtp_from_address" :label="$LANG.Get('fields.smtpFromAddress') + ':'"></NevsTextField>
                <NevsTextField v-model="client.smtp_username" :label="$LANG.Get('fields.smtpUsername') + ':'"></NevsTextField>
                <p v-if="client.smtp_password_set" class="disclaimer saved-password-disclaimer">{{ $LANG.Get('labels.passwordSaved') }}</p>
                <NevsTextField v-model="newSmtpPassword" :label="$LANG.Get('fields.smtpPassword') + ':'"></NevsTextField>
                <div class="smtp-test-button">
                    <NevsButton @click="showSmtpTestModal = true">
                        <i class="fa-solid fa-paper-plane"></i>
                        {{ $LANG.Get('buttons.sendTestEmail') }}
                    </NevsButton>
                </div>
            </template>
        </NevsCard>
        <NevsCard v-if="client.use_smtp" class="form">
            <h2>{{ $LANG.Get('labels.emailTemplates') }}</h2>
            <h3>{{ $LANG.Get('modules.invoices') }}</h3>
            <NevsTextField v-model="client.invoice_email_subject" :label="$LANG.Get('fields.emailSubject') + ':'"></NevsTextField>
            <NevsTextArea v-model="client.invoice_email_body" :label="$LANG.Get('fields.emailBody') + ':'"></NevsTextArea>
            <h3>{{ $LANG.Get('modules.offers') }}</h3>
            <NevsTextField v-model="client.offer_email_subject" :label="$LANG.Get('fields.emailSubject') + ':'"></NevsTextField>
            <NevsTextArea v-model="client.offer_email_body" :label="$LANG.Get('fields.emailBody') + ':'"></NevsTextArea>
            <h3>{{ $LANG.Get('modules.dispatchNotes') }}</h3>
            <NevsTextField v-model="client.dispatch_note_email_subject" :label="$LANG.Get('fields.emailSubject') + ':'"></NevsTextField>
            <NevsTextArea v-model="client.dispatch_note_email_body" :label="$LANG.Get('fields.emailBody') + ':'"></NevsTextArea>
            <template v-if="$store.state.user.client.plus">
                <h3>{{ $LANG.Get('modules.orderNotes') }}</h3>
                <NevsTextField v-model="client.order_note_email_subject" :label="$LANG.Get('fields.emailSubject') + ':'"></NevsTextField>
                <NevsTextArea v-model="client.order_note_email_body" :label="$LANG.Get('fields.emailBody') + ':'"></NevsTextArea>
            </template>
            <h3>{{ $LANG.Get('modules.workOrders') }}</h3>
            <NevsTextField v-model="client.work_order_email_subject" :label="$LANG.Get('fields.emailSubject') + ':'"></NevsTextField>
            <NevsTextArea v-model="client.work_order_email_body" :label="$LANG.Get('fields.emailBody') + ':'"></NevsTextArea>
        </NevsCard>
        <ModalSmtpTest :show="showSmtpTestModal" @done="showSmtpTestModal = false"></ModalSmtpTest>
    </div>
</template>

<script>
import Client from "@/models/Client.js";
import ModalSmtpTest from "@/components/modals/ModalSmtpTest.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsTextArea from "@/components/nevs/NevsTextArea.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";

export default {
    name: "GeneralData",
    components: {ModalSmtpTest, NevsCheckbox, NevsSelect, NevsTextArea, NevsTextField, NevsCard, NevsButton},
    data() {
        return {
            loaded: false,
            client: new Client(),
            clientValidation: new Client({}, true),
            newSmtpPassword: '',
            showSmtpTestModal: false,
            encryptionOptions: [
                {label: this.$LANG.Get('options.smtpEncryptionNone'), value: ''},
                {label: 'TLS', value: 'tls'},
                {label: 'SSL', value: 'ssl'}
            ]
        }
    },
    methods: {
        saveClick() {
            let valid = true;

            if (this.client.use_smtp) {
                if (!this.client.smtp_host || this.client.smtp_host === '') {
                    this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('fields.smtpHost') + ': ' + this.$LANG.Get('alerts.fieldMissing'), type: 'alert'});
                    valid = false;
                } else if (!this.client.smtp_port || parseInt(this.client.smtp_port) <= 0) {
                    this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('fields.smtpPort') + ': ' + this.$LANG.Get('alerts.fieldMissing'), type: 'alert'});
                    valid = false;
                } else if (!this.client.smtp_from_address || this.client.smtp_from_address === '') {
                    this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('fields.smtpFromAddress') + ': ' + this.$LANG.Get('alerts.fieldMissing'), type: 'alert'});
                    valid = false;
                }
            }

            if (valid) {
                this.client.smtp_port = parseInt(this.client.smtp_port);
                this.client.smtp_password = this.newSmtpPassword;
                let vm = this;
                this.$API.APICall('put', 'client', this.client, (data, success) => {
                    if (success) {
                        vm.newSmtpPassword = '';
                        if (vm.client.smtp_password !== '') {
                            vm.client.smtp_password_set = true;
                            vm.client.smtp_password = '';
                        }
                        vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                        vm.$API.APICall('get', 'session', {}, (data, success) => {
                            if (success) {
                                vm.$store.commit('setUser', data.user);
                            }
                        }, false);
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                })
            }
        }
    },
    mounted() {
        this.$store.commit('setHelpOverlay', [
            {
                class: 'help-emails',
                html: this.$LANG.Get('help.emailsSettings'),
                left: 400,
                top: -300,
                width: 350
            }
        ]);
        if (!this.$store.state.user.permissions.includes('ADMINISTRATOR')) {
            this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('alerts.unauthorized'), type: 'alert'});
            this.$router.push('/');
        }
        this.$store.commit('selectMenu', 'settings');
        this.$store.commit('selectSubMenu', 'emailSettings');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.settings'),
                link: null
            },
            {
                label: this.$LANG.Get('modules.emailSettings'),
                link: null
            }
        ]);

        let vm = this;
        this.$API.APICall('get', 'client', {}, (data, success) => {
            if (success) {
                if (data.client !== null) {
                    vm.client.Fill(data.client);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
            vm.$nextTick(() => {
                vm.loaded = true;
            });
        });
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.form {
    max-width: 600px;
    margin-bottom: 10px;
}

.smtp-test-button {
    margin-top: 10px;
}

h3 {
    margin-top: 15px;
    margin-bottom: 5px;
}

.disclaimer {
    font-size: 0.9em;
    opacity: 0.7;
    margin-bottom: 5px;
}

.saved-password-disclaimer {
    opacity: 1;
    color: $font-color;
}
</style>